import moment from "moment";
import { useForm } from "react-hook-form";
import React, { useState, useRef, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationIcon } from "@heroicons/react/outline";
import LoaderButton from "../../../../components/common/LoaderButton/LoaderButton";
import { apiGetRequest, apiPostRequest } from "../../../../helpers/Requests";
import { baseURLImage, validDate } from "../../../../Utils/utils";
import { toast } from "react-toastify";
import RemoveFileAlert from "./Modals/RemoveFileAlert";
import DateRangeWithRegister from "components/common/DateRangePicker/DateRangePickerWithRegister";
const InsuranceSection = ({
  title,
  sectionType,
  fileState,
  setFileState,
  existingFileUrl,
  register,
  watch,
  errors,
  onFileRemoved,
  control,
  Controller,
}) => {
  const { trigger, setValue, setError } = useForm();

  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fileRemoved, setFileRemoved] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const startDate = watch(`${sectionType}_start_date`);
  const endDate = watch(`${sectionType}_end_date`);

  const fileUrl =
    fileState instanceof File
      ? URL.createObjectURL(fileState)
      : existingFileUrl
      ? `https://docs.vertex360.io/${existingFileUrl}`
      : null;

  const fileName =
    fileState instanceof File
      ? fileState.name
      : existingFileUrl
      ? existingFileUrl.split("/").pop()
      : null;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];

    if (file) {
      const isValidFileType = ["application/pdf"].includes(file.type);

      const isValidFileSize = file.size < 3.1 * 1024 * 1024;

      if (!isValidFileType) {
        toast.error(
          `Invalid file type. Please upload a PDF file for ${title}.`
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      if (!isValidFileSize) {
        toast.error(`${title} file size must be smaller than 3MB.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setValue("attachment", e.target.files);
      await trigger("attachment");

      setFileState(file);
      setFileRemoved(false);
    }
  };

  const handleRemove = () => {
    setFileState(null);
    setFileRemoved(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };
  const handleRemoveFile = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };
  const showFileInput = !fileState && (!existingFileUrl || fileRemoved);

  const confirmRemoveFile = async () => {
    setLoading(true);
    if (typeof fileState === "string" && !fileRemoved) {
      const payload = {
        type: sectionType,
        file: existingFileUrl,
      };

      try {
        const response = await apiPostRequest(
          "/sp/remove-attachment-file",
          payload
        );
        if (response.status === 200) {
          toast.success("File removed successfully.");
          setFileState(null);
          setFileRemoved(true);
          onFileRemoved(sectionType);
          if (fileInputRef.current) fileInputRef.current.value = "";
        } else {
          toast.error("Failed to remove file.");
        }
      } catch (error) {
        toast.error(error?.message ?? "Error while removing file");
        setLoading(false);
        closeDialog();
        return;
      }
    }

    setFileRemoved(true);
    setLoading(false);
    closeDialog();
  };

  return (
    <div className='px-6 py-5 bg-gray-50 space-y-6'>
      {sectionType === "indemnity" && (
        <div className='pt-3 text-2xl text-start'>Insurance</div>
      )}
      <div className='text-xl text-gray-800'>{title}</div>

      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='md:col-span-2'>
          <label className='text-sm font-medium text-gray-700'>
            Insurance Provider Name
          </label>
          <input
            {...register(`${sectionType}_provider_name`, {
              maxLength: 100,
            })}
            className='did-floating-input'
            placeholder='e.g. Allianz, AAMI'
          />
        </div>

        <div>
          <label className='text-sm font-medium text-gray-700'>
            Policy Number
          </label>
          <input
            {...register(`${sectionType}_policy_number`, {
              maxLength: 100,
            })}
            className='did-floating-input'
            placeholder='e.g. 123456789'
          />
        </div>

        <div>
          <label className='text-sm font-medium text-gray-700'>
            Insurance Type
          </label>
          <input
            {...register(`${sectionType}_insurance_type`, {
              maxLength: 100,
            })}
            className='did-floating-input'
            placeholder='e.g. Liability, Health'
          />
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='md:col-span-2'>
          <label className='text-sm font-medium text-gray-700'>
            Attachment (Optional)
          </label>

          {!fileState && (!existingFileUrl || fileRemoved) && (
            <input
              key={fileRemoved ? "removed" : "new"}
              ref={fileInputRef}
              type='file'
              onChange={handleFileChange}
              accept='.pdf'
              className={`block w-full mt-1 text-sm text-gray-600 border border-gray-300 rounded-md file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 ${
                isUploading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:file:bg-blue-200"
              }`}
            />
          )}
          {!fileState && (!existingFileUrl || fileRemoved) && (
            <span className='text-sm text-gray-500'>
              Upload a PDF file only. File size must be less than 3 MB.
            </span>
          )}
          {(fileState || (existingFileUrl && !fileRemoved)) && (
            <div className='flex items-center justify-between bg-gray-100 rounded-lg px-3 py-2 border border-gray-200'>
              <div className='flex flex-col max-w-[70%]'>
                <a
                  href={fileUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-[#4564AA] underline truncate'
                >
                  {fileName}
                </a>
              </div>

              <div className='flex items-center space-x-2'>
                {!isUploading && (
                  <button
                    type='button'
                    onClick={() => {
                      if (typeof fileState === "string" && !fileRemoved) {
                        setIsDialogOpen(true);
                      } else {
                        handleRemove();
                      }
                    }}
                    className='text-red-500 text-sm font-medium hover:underline'
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <label className='text-sm font-medium text-gray-700'>
            Policy Start Date
          </label>
          <Controller
            name={`${sectionType}_start_date`}
            control={control}
            rules={{ required: false }}
            render={({ field, fieldState }) => (
              <DateRangeWithRegister
                isSingleDate
                field={field}
                fieldState={fieldState}
                placeholder='Select Policy Start Date'
                showCalendarIcon
              />
            )}
          />
          {/* <input
            type='date'
            min={moment().format("DD-MM-YYYY")}
            {...register(`${sectionType}_start_date`)}
            className='did-floating-input'
          /> */}
        </div>

        <div>
          <label className='text-sm font-medium text-gray-700'>
            Policy End Date
          </label>

          {/* <input
            type='date'
            {...register(`${sectionType}_end_date`, {
              validate: (value) => {
                const start = watch(`${sectionType}_start_date`);
                if (!start || !value) return true;
                return (
                  moment(value).isSameOrAfter(moment(start)) ||
                  "End date must be after policy start date"
                );
              },
            })}
            className='did-floating-input'
          /> */}
          <Controller
            name={`${sectionType}_end_date`}
            control={control}
            rules={{
              required: false,
              validate: (value) => {
                const start = watch(`${sectionType}_start_date`);
                if (!start || !value) return true;
                return (
                  moment(value).isSameOrAfter(moment(start)) ||
                  "End date must be after policy start date"
                );
              },
            }}
            render={({ field, fieldState }) => (
              <DateRangeWithRegister
                isSingleDate
                field={field}
                fieldState={fieldState}
                placeholder='Select Policy End Date'
                showCalendarIcon
              />
            )}
          />
          {errors?.[`${sectionType}_end_date`] && (
            <span className='text-red-500 text-sm'>
              {errors[`${sectionType}_end_date`].message}
            </span>
          )}
        </div>
      </div>

      <RemoveFileAlert
        isOpen={isDialogOpen}
        loading={loading}
        onClose={closeDialog}
        onConfirm={confirmRemoveFile}
      />
    </div>
  );
};
export default InsuranceSection;
